import { User } from 'models/User'
import { useTranslation } from 'react-i18next'
import { buildAbsoluteMedicareMyUrl } from 'utils/urls/buildAbsoluteMedicareMyUrl'
import { Button } from 'components/Button/Button'
import { IconInfo } from 'components/Icon/IconInfo'
import { IconCard } from 'components/Icon/IconCard'
import { IconPeople } from 'components/Icon/IconPeople'
import { MyInternalLink, MyLinkSiteActivityPropsV2 } from 'components/MyLink'
import classNames from 'classnames'
import { Component } from '@cms-max/medicare-api-sdk'

interface HomeHeaderColumnProps {
  row1: React.ReactNode
  row2: React.ReactNode
  row3: React.ReactNode
}

/**
 * Helper component for rendering HomeHeaderButtons.
 * The second row grows vertically  while the first and third do not
 */
const HomeHeaderColumn: React.FC<HomeHeaderColumnProps> = ({ row1, row2, row3 }) => {
  const wrapperClassNames = classNames(
    'ds-l-lg-col--4 ds-l-col--12 my-u-mb--15',
    'ds-u-display--flex ds-u-flex-direction--column'
  )
  return (
    <div className={wrapperClassNames}>
      <div style={{ flexBasis: 0, flexGrow: 0 }}>
        {row1}
      </div>
      <div style={{ flexBasis: 1, flexGrow: 1 }}>
        {row2}
      </div>
      <div style={{ flexBasis: 0, flexGrow: 0 }}>
        {row3}
      </div>
    </div>
  )
}

const HomeHeaderH2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="ds-text-heading--xl my-u-mb--2 my-u-lg-mb--6">{children}</h2>
)

const HomeHeaderRow2Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className='ds-u-margin-bottom--2'>
    {children}
  </div>
)

export interface HomeHeaderButtonsProps {
  formattedMailingAddress: string,
  name: {
    first?: User['firstName'],
    middle?: User['middleName'],
    last?: User['lastName']
  }
  openNameAddressDrawer: () => void,
}

export const HomeHeaderButtons: React.FC<HomeHeaderButtonsProps> = ({
  formattedMailingAddress,
  name,
  openNameAddressDrawer
}) => {
  const { t, i18n } = useTranslation('accountSettings')
  const { language } = i18n

  const siteActivityGetMedicareCard: MyLinkSiteActivityPropsV2 = {
    core: {
      component: Component.AccountSettings,
      elementId: 'accountsettings_getcard_click',
      coreClickData: {
        linkText: t('btns.top2'),
        linkUrl: buildAbsoluteMedicareMyUrl('/account-settings/get-my-medicare-card')
      }
    }
  }

  const siteActivityUpdateNameOrAddress: MyLinkSiteActivityPropsV2 = {
    core: {
      component: Component.AccountSettings,
      elementId: 'accountsettings_updatenameoraddressmodal_click',
      coreClickData: {
        linkText: t('btns.top1')
      }
    }
  }

  const siteActivityAuthRep: MyLinkSiteActivityPropsV2 = {
    core: {
      component: Component.AccountSettings,
      elementId: 'accountsettings_manageauthrep_click',
      coreClickData: {
        linkText: t('btns.top3'),
        linkUrl: buildAbsoluteMedicareMyUrl('/account-settings/auth-rep')
      }
    }
  }

  return (
    <div className='ds-l-row ds-u-flex-wrap--wrap'>
      <HomeHeaderColumn
        row1={<HomeHeaderH2>{t('paragraph.nameAndAddressTitle')}</HomeHeaderH2>}
        row2={(
          <HomeHeaderRow2Wrapper>
            <p className="my-u-font-weight--bold my-u-my--0">{name.first?.toLocaleUpperCase(language)} {name.middle?.toLocaleUpperCase(language)} {name.last?.toLocaleUpperCase(language)}</p>
            <p className="my-u-mb--0">{formattedMailingAddress}</p>
          </HomeHeaderRow2Wrapper>
        )}
        row3={(
          <Button
            className="ds-c-button"
            onClick={openNameAddressDrawer}
            data-testid="acct-name-address"
            siteActivityProps={siteActivityUpdateNameOrAddress}
          >
            <div className="ds-u-display--flex">
              <IconInfo aria-hidden className='my-c-my-svg-icon--primary-alt-dark my-u-mr--2'/>
              <span className="my-u-line-height-text-btn">{t('btns.top1')}</span>
            </div>
          </Button>
        )}
      />
      <HomeHeaderColumn
        row1={<HomeHeaderH2>{t('paragraph.medicareCardTitle')}</HomeHeaderH2>}
        row2={(
          <HomeHeaderRow2Wrapper>
            <p className="my-u-mb--0">{t('paragraph.medicareCardInfo')}</p>
          </HomeHeaderRow2Wrapper>
        )}
        row3={(
          <MyInternalLink
            className="ds-c-button"
            to='/account-settings/get-my-medicare-card'
            siteActivityProps={siteActivityGetMedicareCard}
          >
            <div className="ds-u-display--flex">
              <IconCard aria-hidden className="my-u-mr--2"/>
              <div className="my-u-line-height-text-btn">{t('btns.top2')}</div>
            </div>
          </MyInternalLink>
        )}
      />
      <HomeHeaderColumn
        row1={<HomeHeaderH2>{t('paragraph.myRepresentativesTitle')}</HomeHeaderH2>}
        row2={(
          <HomeHeaderRow2Wrapper>
            <p className="my-u-mb--0">{t('paragraph.myRepresentativesInfo')}</p>
          </HomeHeaderRow2Wrapper>
        )}
        row3={(
          <MyInternalLink
            className='ds-c-button'
            to='/account-settings/auth-rep/'
            siteActivityProps={siteActivityAuthRep}
          >
            <div className="ds-u-display--flex">
              <IconPeople aria-hidden className="my-u-mr--2" />
              <div className="my-u-line-height-text-btn">{t('btns.top3')}</div>
            </div>
          </MyInternalLink>
        )}
      />
    </div>
  )
}
